/**
 * CACHE ARCHITECTURE (4-layer):
 * ──────────────────────────────
 * Layer 0: TanStack memory cache (in-memory, instant, same session)
 * Layer 1: persistQueryClient (localStorage, instant, 24h max age)
 * Layer 2: Live API (FastAPI backend, background refetch)
 * Layer 3: /snapshot/blog/*.json (server-side, auto-generated by backend)
 *
 * FLOW ON REFRESH:
 *   1. persistQueryClient hydrates from localStorage → render instantly
 *   2. API call fires in background → updates if changed (no flicker)
 *   3. If API fails → snapshot JSON loads → persists to localStorage
 *
 * CACHE INVALIDATION:
 *   - Static CACHE_VERSION: developer-bumped on deploy (baseline)
 *   - Dynamic: fetches snapshot _meta.generatedAt on startup; if it differs
 *     from the stored version, localStorage is cleared so returning visitors
 *     always see the latest content.
 */

import { QueryClient } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds before background refetch
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - cached data retained for offline
      refetchOnWindowFocus: true, // Refresh when user returns to tab
      refetchOnReconnect: "always", // Refresh when network recovers
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * attemptIndex, 3000),
    },
  },
});

/* ── Persistence setup ────────────────────────────────
 * Only runs in the browser (not during SSR or build). */
if (typeof window !== "undefined") {
  try {
    const persister = createSyncStoragePersister({
      key: "exxonim-query-cache",
      storage: window.localStorage,
      serialize: JSON.stringify,
      deserialize: JSON.parse,
    });

    // ── Static cache version (developer-bumped on deploy) ──
    const CACHE_VERSION = "2026-07-22-2";
    const storedVersion = localStorage.getItem("exxonim-cache-version");
    if (storedVersion !== CACHE_VERSION) {
      localStorage.removeItem("exxonim-query-cache");
      localStorage.setItem("exxonim-cache-version", CACHE_VERSION);
    }

    persistQueryClient({
      queryClient: queryClient as any,
      persister,
      maxAge: 1000 * 60 * 60 * 24, // 24 hours
    });

    // ── Dynamic cache invalidation from snapshot version ──
    // Fetch the blog snapshot's _meta.generatedAt timestamp.
    // If it's newer than what we stored, clear localStorage so the next
    // page load fetches fresh data.  This handles the case where an admin
    // publishes/drafts content and a returning visitor still has stale data.
    // Uses a short timeout so it never blocks rendering.
    (async () => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 500);
        const res = await fetch("/snapshot/blog/blog-posts.json", {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        clearTimeout(timer);
        if (!res.ok) return;
        const json = await res.json();
        const snapshotVersion = json?._meta?.generatedAt;
        if (!snapshotVersion) return;
        const storedSnapshotVersion = localStorage.getItem("exxonim-snapshot-version");
        if (storedSnapshotVersion !== snapshotVersion) {
          localStorage.setItem("exxonim-snapshot-version", snapshotVersion);
          // Only clear query cache if the snapshot actually changed
          // (not on first visit when storedSnapshotVersion is null)
          if (storedSnapshotVersion !== null) {
            localStorage.removeItem("exxonim-query-cache");
            // Force TanStack Query to refetch everything
            queryClient.invalidateQueries();
          }
        }
      } catch {
        // Snapshot not available (first visit, CORS, etc.) - ignore
      }
    })();
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded, etc.)
  }
}
